import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Download, Database, Calendar, FileSpreadsheet, HardDrive } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Backup() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  const handleFullBackup = async () => {
    setIsDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/backup/full`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from content-disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'westaw_backup.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Full backup downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download backup');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDateRangeBackup = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date cannot be after end date');
      return;
    }

    setIsDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/backup/full`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date: startDate,
          end_date: endDate
        },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const filename = `westaw_backup_${startDate}_to_${endDate}.xlsx`;
      link.setAttribute('download', filename);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success(`Backup for ${startDate} to ${endDate} downloaded successfully!`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download backup');
    } finally {
      setIsDownloading(false);
    }
  };

  // Set quick date ranges
  const setLastMonth = () => {
    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    setStartDate(lastMonth.toISOString().split('T')[0]);
    setEndDate(lastMonthEnd.toISOString().split('T')[0]);
  };

  const setLastQuarter = () => {
    const today = new Date();
    const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3 - 3, 1);
    const quarterEnd = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 0);
    setStartDate(quarterStart.toISOString().split('T')[0]);
    setEndDate(quarterEnd.toISOString().split('T')[0]);
  };

  const setThisYear = () => {
    const today = new Date();
    const yearStart = new Date(today.getFullYear(), 0, 1);
    setStartDate(yearStart.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  };

  const setThisMonth = () => {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    setStartDate(monthStart.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight font-heading" data-testid="backup-title">
          Data Backup
        </h1>
        <p className="text-slate-600 mt-2">Download your factory inventory data for local backup</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Full Backup Card */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Database className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-xl font-semibold">Full Backup</CardTitle>
                <CardDescription>Download all data from all modules</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-medium text-slate-900 mb-2">Includes:</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li className="flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-slate-400" />
                    Items Master Data
                  </li>
                  <li className="flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-slate-400" />
                    Suppliers Master Data
                  </li>
                  <li className="flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-slate-400" />
                    Stock Receipts
                  </li>
                  <li className="flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-slate-400" />
                    Stock Issues
                  </li>
                  <li className="flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-slate-400" />
                    Material Reconciliation
                  </li>
                  <li className="flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-slate-400" />
                    Turned Parts (Stock-In & Stock-Out)
                  </li>
                  <li className="flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-slate-400" />
                    Payments
                  </li>
                </ul>
              </div>
              
              <Button 
                onClick={handleFullBackup} 
                disabled={isDownloading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg"
                data-testid="full-backup-button"
              >
                <HardDrive className="w-5 h-5 mr-2" />
                {isDownloading ? 'Downloading...' : 'BACKUP'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Date Range Backup Card */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-sky-100 rounded-lg">
                <Calendar className="w-6 h-6 text-sky-600" />
              </div>
              <div>
                <CardTitle className="text-xl font-semibold">Date Range Backup</CardTitle>
                <CardDescription>Download data for a specific date range</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {/* Quick Date Buttons */}
              <div>
                <Label className="text-sm text-slate-600">Quick Select:</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={setThisMonth}
                    className="text-xs"
                  >
                    This Month
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={setLastMonth}
                    className="text-xs"
                  >
                    Last Month
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={setLastQuarter}
                    className="text-xs"
                  >
                    Last Quarter
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={setThisYear}
                    className="text-xs"
                  >
                    This Year
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    data-testid="backup-start-date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    data-testid="backup-end-date"
                  />
                </div>
              </div>

              {startDate && endDate && (
                <div className="bg-sky-50 p-3 rounded-lg text-sm text-sky-700">
                  <strong>Selected Range:</strong> {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
                </div>
              )}
              
              <Button 
                onClick={handleDateRangeBackup} 
                disabled={isDownloading || !startDate || !endDate}
                className="w-full bg-sky-600 hover:bg-sky-700 text-white py-6 text-lg"
                data-testid="date-range-backup-button"
              >
                <Download className="w-5 h-5 mr-2" />
                {isDownloading ? 'Downloading...' : 'Download Range Backup'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <FileSpreadsheet className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h4 className="font-medium text-amber-900">Backup File Format</h4>
              <p className="text-sm text-amber-700 mt-1">
                All backups are downloaded as Excel (.xlsx) files with multiple sheets - one for each data module. 
                You can open these files in Microsoft Excel, Google Sheets, or any compatible spreadsheet application.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default Backup;
